<template>
  <v-toolbar flat dense color="color3 background--text">
    <v-btn text color="background" @click="onHomeClick()" v-if="!isLessThanThresholdWindowWidth">
      <v-icon left>mdi-folder-home</v-icon>
      <span>Home</span>
      <v-icon right>mdi-chevron-right</v-icon>
    </v-btn>

    <v-toolbar
      v-if="!isLessThanThresholdWindowWidth"
      flat
      dense
      color="color3 background--text"
      style="padding=0px !important;"
    >
      <div background--text v-for="(value, idx) in getFolderBreadCrumbs" :key="idx">
        <span>{{value}}</span>
        <v-icon right color="background">mdi-chevron-right</v-icon>
      </div>
    </v-toolbar>

    <v-spacer></v-spacer>

    <v-tooltip top v-if="isLessThanThresholdWindowWidth">
      <template #activator="{on}">
        <v-btn text v-on="on" color="background" @click="onHomeClick()">
          <v-icon>mdi-folder-home</v-icon>
        </v-btn>
      </template>
      <span>Home folder</span>
    </v-tooltip>

    <v-tooltip top>
      <template v-slot:activator="{on}">
        <div ref="refreshBtn">
          <v-btn text v-on="on" color="background" @click="refreshClick()">
          <v-icon>mdi-refresh-circle</v-icon>
        </v-btn>
        </div>
      </template>
      <span>Refresh</span>
    </v-tooltip>

    <v-dialog v-model="dialogs.createNewFolderDialog" max-width="500">
      <template #activator="{on: dialog}">
        <v-tooltip top>
          <template #activator="{on: tooltip}">
            <v-btn
              v-on="{...dialog, ...tooltip}"
              text
              :disabled="!createFolderActive"
              color="background"
            >
              <v-icon>mdi-folder-plus</v-icon>
            </v-btn>
          </template>
          <span>Create Folder</span>
        </v-tooltip>
      </template>
      <v-card>
        <v-form v-model="formValid.createNewFolder" ref="createNewFolderForm">
          <v-card-title class="color5 background--text">Create new folder</v-card-title>
          <v-container>
            <v-row>
              <v-col>
                <v-text-field
                  label="Folder name"
                  v-model="formValues.newFolderName"
                  :rules="[validationRules.newFolderRules]"
                  counter="30"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="background" @click="dialogs.createNewFolderDialog=false">Cancel</v-btn>
            <v-btn
              color="color5 background--text"
              :disabled="!newFolderFormValid"
              @click="onCreateFolder()"
            >Create</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.uploadNewFileDialog" max-width="500">
      <template #activator="{on: dialog}">
        <v-tooltip top>
          <template #activator="{on: tooltip}">
            <v-btn
              v-on="{...dialog, ...tooltip}"
              text
              :disabled="!uploadNewFileActive"
              color="background"
            >
              <v-icon>mdi-cloud-upload</v-icon>
            </v-btn>
          </template>
          <span>Upload new file</span>
        </v-tooltip>
      </template>
      <v-card>
        <v-form v-model="formValid.uploadNewFile" ref="uploadNewFileForm">
          <v-card-title class="color5 background--text">Upload new file</v-card-title>
          <v-container>
            <v-layout row justify-center align-center>
              <v-flex xs10>
                <v-file-input
                  label="Select file to upload"
                  max-width="500"
                  show-size
                  prepend-icon="mdi-file"
                  counter
                  accept=".pdf, .doc, .docx, .xls, .xlsx, .ppt"
                  :rules="[validationRules.fileRules]"
                  @change="onFileSelect"
                  ref="fileToUpload"
                  @click:clear="fileClear"
                  v-model="formValues.fileInput"
                ></v-file-input>
              </v-flex>
              <v-flex xs10>
                <v-text-field
                  label="Display name"
                  counter="150"
                  v-model="formValues.displayName"
                  :rules="[validationRules.displayNameRules]"
                ></v-text-field>
              </v-flex>
              <v-flex xs10>
                <v-textarea
                  outlined
                  label="Description about the file"
                  v-model="formValues.fileDescription"
                  :rules="[validationRules.fileDescriptionRules]"
                  counter="250"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="background" @click="dialogs.uploadNewFileDialog=false">Cancel</v-btn>
            <v-btn
              color="color5 background--text"
              :disabled="!newFileFormValid"
              @click="onUploadNewFile()"
            >Upload</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-tooltip top>
      <template #activator="{on}">
        <v-btn text @click="onDelete" :disabled="!deleteFolderActive" color="background" v-on="on">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
      <span>Delete current folder</span>
    </v-tooltip>
  </v-toolbar>
</template>

<script>
import { mapActions } from "vuex";
import { mapGetters } from "vuex";
import { eventBus } from "@/main.js";

export default {
  name: "ExplorerToolbar",
  data() {
    return {
      formValid: {
        createNewFolder: false,
        uploadNewFile: false
      },
      dialogs: {
        createNewFolderDialog: false,
        uploadNewFileDialog: false
      },
      validationRules: {
        newFolderRules: value =>
          (!!value && this.validFolderName(value) && value.length <= 30) ||
          "Please enter a valid folder name",
        fileRules: value =>
          (!!value && value.size < 10000000) ||
          "File size should be less than 10MB",
        displayNameRules: value =>
          (!!value && value.length <= 150) || "Please enter a valid description.",
        fileDescriptionRules: value =>
          (!!value && value.length <= 250) || "Please enter a valid description."
      },
      formValues: {
        newFolderName: undefined,
        fileDataUrl: undefined,
        newFile: undefined,
        fileInput: undefined,
        fileDescription: undefined,
        displayName: undefined
      }
    };
  },
  methods: {
    ...mapActions([
      "clearActiveFolder",
      "createSubFolder",
      "deleteFolder",
      "fetchFileContainerFromDatabase",
      "updateActiveFolder",
      "setErrorMessage",
      "uploadNewFile",
      "refreshFilemanager"
    ]),
    onUploadNewFile() {
      this.$refs.uploadNewFileForm.validate();
      this.uploadNewFile({
        newFile: this.formValues.newFile,
        description: this.formValues.fileDescription,
        displayName: this.formValues.displayName,
        userId: this.user.id
      });
      this.dialogs.uploadNewFileDialog = false;
      this.$refs.uploadNewFileForm.reset();
    },
    onCreateFolder() {
      this.$refs.createNewFolderForm.validate();
      this.createSubFolder(this.formValues.newFolderName);
      this.dialogs.createNewFolderDialog = false;
      this.$refs.createNewFolderForm.reset();
    },
    onDelete() {
      this.deleteFolder();
      this.onHomeClick();
    },
    onHomeClick() {
      eventBus.$emit("homeClicked");
      this.clearActiveFolder();
    },
    refreshClick() {
      this.refreshFilemanager();
    },
    validFolderName(name) {
      var re = /^[a-zA-Z0-9._ ,-]*$/;
      return re.test(name);
    },
    onFileSelect(file) {
      if (file) {
        let filename = file.name;
        if (
          !(
            filename.toLowerCase().endsWith(".pdf") ||
            filename.toLowerCase().endsWith(".doc") ||
            filename.toLowerCase().endsWith(".docx") ||
            filename.toLowerCase().endsWith(".xlsx") ||
            filename.toLowerCase().endsWith(".xls") ||
            filename.toLowerCase().endsWith(".ppt")
          )
        ) {
          this.setErrorMessage(
            "Please select a valid file (.doc, .docx, .pdf, .ppt, .xls, .xlsx)"
          );
        }
        const fileReader = new FileReader();
        fileReader.addEventListener("load", () => {
          this.formValues.fileDataUrl = fileReader.result;
        });
        fileReader.readAsDataURL(file);
        this.formValues.newFile = file;
      }
    },
    fileClear() {
      this.formValues.newFile = null;
      this.formValues.fileDataUrl = undefined;
    }
  },
  computed: {
    ...mapGetters([
      "getActiveFolder",
      "getFolderBreadCrumbs",
      "user",
      "getWindowWidth"
    ]),
    newFolderFormValid() {
      if (this.formValues.newFolderName) {
        return (
          this.formValues.newFolderName !== "" &&
          this.validFolderName(this.formValues.newFolderName) &&
          this.formValues.newFolderName.length <= 30
        );
      }
      return false;
    },
    newFileFormValid() {
      if (this.formValues.newFile && this.formValues.fileDescription) {
        return (
          this.formValues.newFile !== null &&
          this.formValues.fileDescription.length <= 250 &&
          this.formValues.fileDescription !== ""
        );
      }
      return false;
    },
    createFolderActive() {
      if (this.getActiveFolder.id) {
        return this.getActiveFolder.id !== "" && this.getActiveFolder.level < 3;
      } else {
        return false;
      }
    },
    uploadNewFileActive() {
      if (this.getActiveFolder.id) {
        return this.getActiveFolder.id !== "";
      } else {
        return false;
      }
    },
    deleteFolderActive() {
      if (this.getActiveFolder.id) {
        return (
          this.getActiveFolder.id !== "" && this.getActiveFolder.level !== 1
        );
      } else {
        return false;
      }
    },
    isLessThanThresholdWindowWidth() {
      const thresholdWindowWidth = 850;
      if (this.getWindowWidth >= thresholdWindowWidth) {
        return false;
      }
      return true;
    }
  },
  mounted() {
    this.onHomeClick();
  }
};
</script>